import { useState, useDeferredValue } from 'react';
import { useIssuePicker } from '@/hooks/useIssueLinks.js';
import type { JiraIssuePickerResult } from '@shared/types.js';

interface IssueKeysInputProps {
  value: JiraIssuePickerResult[];
  onChange: (value: JiraIssuePickerResult[]) => void;
  placeholder?: string;
  label: string;
}

/** Pick several work items by key or summary; the chosen ones show as removable chips. */
export function IssueKeysInput({ value, onChange, placeholder, label }: IssueKeysInputProps) {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const picker = useIssuePicker(deferredQuery, '');
  const chosen = new Set(value.map((v) => v.key));
  const found = (picker.data ?? []).filter((r) => !chosen.has(r.key));
  // A full key can be added even when the search has nothing for it (a ticket made a moment ago)
  const typedKey = query.trim().toUpperCase();
  const direct = /^[A-Z][A-Z0-9_]*-\d+$/.test(typedKey) && !chosen.has(typedKey) && !found.some((r) => r.key === typedKey)
    ? [{ key: typedKey, summary: '' }]
    : [];
  const results = [...direct, ...found];
  const showResults = query.trim().length >= 2;

  function add(item: JiraIssuePickerResult) {
    onChange([...value, item]);
    setQuery('');
  }

  return (
    <div className="relative">
      {value.length > 0 && (
        <ul className="mb-1.5 flex flex-wrap gap-1.5">
          {value.map((v) => (
            <li key={v.key} className="flex max-w-full items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-2 py-1 text-sm">
              <span className="shrink-0 font-medium text-blue-700">{v.key}</span>
              {v.summary && <span className="min-w-0 truncate text-gray-700">{v.summary}</span>}
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x.key !== v.key))}
                className="text-gray-400 hover:text-gray-700"
                aria-label={`Remove ${v.key}`}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          // Enter picks the first match instead of submitting the form around it
          e.preventDefault();
          if (showResults && results[0]) add(results[0]);
        }}
        placeholder={placeholder}
        className="w-full rounded-md border px-3 py-2 text-sm"
        aria-label={label}
      />
      {showResults && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-white py-1 shadow-lg">
          {picker.isFetching && results.length === 0 && <li className="px-3 py-2 text-xs text-gray-400">Searching...</li>}
          {!picker.isFetching && results.length === 0 && <li className="px-3 py-2 text-xs text-gray-400">No matching work items</li>}
          {results.map((r) => (
            <li key={r.key}>
              <button type="button" onClick={() => add(r)} className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-blue-50">
                <span className="shrink-0 font-medium text-blue-600">{r.key}</span>
                <span className="truncate text-gray-700">{r.summary || 'Add this work item'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
